/**
 * quotable.to — the family hub world.
 *
 * The vendored pools engine paints a still night basin (world.ts); the packs
 * the platform serves ride on it as glyphs (artifacts.ts). One glyph is focused
 * at a time, its title/blurb shown on the card; a heavy horizontal drag turns
 * focus from one to the next at the family's fixed pace (turn.ts, law 1), and
 * the focused title is material that sways with the device (tilt.ts, law 3).
 */

import { startWorld } from "./world.js";
import { loadPacks } from "./registry.js";
import { layoutArtifacts, type Artifact } from "./artifacts.js";
import { createTurn } from "./turn.js";
import { cardWeight, requestTiltPermission, startTilt } from "./tilt.js";
import { unveil, type Unveil } from "./unveil.js";
import * as sound from "./audio.js";
import { createPulse, type PulseClient } from "./pulse.js";
import { mulberry32 } from "./pool/core/prng.js";

/** The three sibling pools whose keeps ripple the shared basin. */
const FAMILY_APPS = ["stoke", "becoming", "proverb"] as const;

/** Drag distance (px) mapping to a full ±1 turn — matches the card apps. */
const TURN_DRAG_PX = 64;
/** Fraction of a full drag past which a release commits rather than springs back. */
const COMMIT_FRACTION = 0.5;

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

function tap(pattern: number | number[]): void {
  // Haptics are the third sense; absent on desktop, and that is fine.
  if (typeof navigator.vibrate === "function") navigator.vibrate(pattern);
}

/** A deterministic per-day seed, so the basin's arrangement holds all day. */
function stageSeed(): number {
  const day = new Date().toISOString().slice(0, 10);
  let h = 2166136261;
  for (let i = 0; i < day.length; i++) {
    h ^= day.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const stage = el<HTMLElement>("stage");
const basin = el<HTMLElement>("basin");
const card = el<HTMLElement>("card");
const cardTitle = el<HTMLElement>("card-title");
const cardBlurb = el<HTMLElement>("card-blurb");

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const seed = stageSeed();
// The world mounts into #basin (a background host mountPool clears); the card
// lives in #stage above it, so the pool canvas never wipes the card.
const world = startWorld(basin, { seed, reducedMotion });
const turn = createTurn(card);

let artifacts: Artifact[] = [];
let activeTilt: { stop(): void } | null = null;
let activeUnveil: Unveil | null = null;

/** Show the focused pack on the card and re-anchor the tilt to its title. */
function paintCard(): void {
  const i = world.focusedIndex();
  activeTilt?.stop();
  activeTilt = null;
  // Finish any in-flight unveil before starting the next, so an interrupted
  // blurb's remaining timers can't fire tones after we've moved on.
  activeUnveil?.finish();
  activeUnveil = null;
  const artifact = i >= 0 ? artifacts[i] : undefined;
  if (!artifact) {
    // Empty basin: the card collapses to nothing rather than an error (law 6).
    cardTitle.textContent = "";
    cardBlurb.textContent = "";
    return;
  }
  cardTitle.textContent = artifact.pack.title;
  // Law 3: a heavier pack (denser title) sways less. tilt.ts falls back to a
  // fixed, gentle static tilt when the device offers no orientation.
  activeTilt = startTilt(cardTitle, cardWeight(artifact.pack.title));
  // Law 4: the blurb arrives phrase by phrase, each landing with a haptic pulse
  // and a tone — reading as a felt cadence, not an instant paint.
  activeUnveil = unveil(cardBlurb, artifact.pack.blurb, {
    reducedMotion,
    onPhrase(index, total) {
      tap(14);
      sound.phrase(index, total);
    },
  });
}

/** Turn focus one step. The commit paces itself at MIN_TURN_MS no matter the
 *  flick, then swaps the focused glyph and card once the page is back at rest. */
function advance(direction: 1 | -1): void {
  if (artifacts.length === 0) return;
  turn.commit(direction, () => {
    world.setFocus(world.focusedIndex() + direction);
    paintCard();
  });
}

// --- the heavy drag -------------------------------------------------------
let dragging = false;
let startX = 0;
let lastDx = 0;

stage.addEventListener("pointerdown", (ev) => {
  if (turn.isCommitting()) return;
  dragging = true;
  startX = ev.clientX;
  lastDx = 0;
  stage.setPointerCapture(ev.pointerId);
});

stage.addEventListener("pointermove", (ev) => {
  if (!dragging || turn.isCommitting()) return;
  lastDx = ev.clientX - startX;
  // Dragging left (dx < 0) turns toward the next glyph, matching the card apps.
  turn.progress(clamp(-lastDx / TURN_DRAG_PX, -1, 1));
});

function endDrag(): void {
  if (!dragging) return;
  dragging = false;
  if (Math.abs(lastDx) >= TURN_DRAG_PX * COMMIT_FRACTION) {
    advance(lastDx < 0 ? 1 : -1);
  } else {
    turn.cancel();
  }
}

stage.addEventListener("pointerup", endDrag);
stage.addEventListener("pointercancel", endDrag);

// Arrow keys turn too — desktop and assistive navigation, same pacing.
window.addEventListener("keydown", (ev) => {
  if (turn.isCommitting()) return;
  if (ev.key === "ArrowRight") advance(1);
  else if (ev.key === "ArrowLeft") advance(-1);
});

// iOS gates device orientation behind a user gesture; ask once, on first touch.
stage.addEventListener(
  "pointerdown",
  () => {
    void requestTiltPermission();
  },
  { once: true },
);

// Load the family's packs and lay them out as glyphs on the water. loadPacks
// never rejects — on any failure the basin simply stays unpopulated (law 6).
void loadPacks().then((packs) => {
  artifacts = layoutArtifacts(packs, seed);
  world.setArtifacts(artifacts);
  paintCard();
});

// --- ambient co-presence (law 2, zero-metric) -----------------------------
// One pulse client per sibling app, all same-origin. A keep anywhere in the
// family arrives here as a delta and becomes a ripple on the water plus a soft
// chime — never a name, never a count, never a number in the DOM. dailyCardId
// returns null: the hub has no single card being read, so only the generic
// "someone somewhere kept" signal applies. All failures are swallowed inside
// pulse.ts, so an offline or down platform is simply quieter.
const rippleRand = mulberry32(seed ^ 0x9e3779b9);
const pulses: PulseClient[] = FAMILY_APPS.map((app) => {
  const pulse = createPulse("", app, () => null);
  pulse.onRemoteKeep(() => {
    // A drop lands somewhere on the water's lower field, where the eye rests.
    world.ripple(rippleRand(), 0.45 + rippleRand() * 0.4);
    sound.chime();
  });
  pulse.start();
  return pulse;
});

function teardown(): void {
  activeTilt?.stop();
  activeUnveil?.finish();
  for (const pulse of pulses) pulse.stop();
  world.destroy();
}

window.addEventListener("pagehide", teardown, { once: true });
window.addEventListener("beforeunload", teardown, { once: true });
